import { OnlinePaymentDetails, PaymentItem } from '../types';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Create online payment record in Firebase when Flutterwave payment is initiated
export const createOnlinePayment = async (
  userId: string,
  items: PaymentItem[],
  total: number,
  customerInfo: any,
  transactionRef: string,
  referredBy?: string
): Promise<string> => {
  try {
    // Validate inputs
    if (!userId || !items || items.length === 0 || !total || !customerInfo || !transactionRef) {
      throw new Error('Missing required parameters for online payment');
    }

    const paymentId = `ONLINE_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const onlinePayment: OnlinePaymentDetails = {
      id: paymentId,
      userId,
      items,
      total,
      currency: 'GHS',
      customerInfo,
      paymentMethod: 'online',
      paymentDetails: {
        method: 'Flutterwave',
        transactionRef: transactionRef,
      },
      referredBy,
      status: 'pending',
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Clean the data before sending to Firestore (remove any undefined values)
    const cleanOnlinePayment = JSON.parse(JSON.stringify(onlinePayment));
    
    // Save to OnlinePayment collection
    const onlinePaymentRef = doc(db, 'OnlinePayment', paymentId);
    await setDoc(onlinePaymentRef, cleanOnlinePayment);

    // Store payment ID in localStorage for later deletion
    localStorage.setItem('onlinePaymentId', paymentId);
    localStorage.setItem('onlinePaymentTransactionRef', transactionRef);

    console.log('Online payment record created:', paymentId);
    return paymentId;
  } catch (error) {
    console.error('Error creating online payment:', error);
    throw new Error('Failed to create online payment record');
  }
};

// Delete online payment record when payment is successful
export const deleteOnlinePayment = async (transactionRef: string): Promise<boolean> => {
  try {
    // Get the payment ID from localStorage
    const paymentId = localStorage.getItem('onlinePaymentId');
    const storedTransactionRef = localStorage.getItem('onlinePaymentTransactionRef');
    
    if (!paymentId || storedTransactionRef !== transactionRef) {
      console.warn('Payment ID not found in localStorage or transaction ref mismatch');
      return false;
    }

    // Delete the document from OnlinePayment collection
    const onlinePaymentRef = doc(db, 'OnlinePayment', paymentId);
    await deleteDoc(onlinePaymentRef);

    // Clean up localStorage
    localStorage.removeItem('onlinePaymentId');
    localStorage.removeItem('onlinePaymentTransactionRef');

    console.log('Online payment record deleted successfully:', paymentId);
    return true;
  } catch (error) {
    console.error('Error deleting online payment:', error);
    return false;
  }
};

// Get online payment by transaction reference
export const getOnlinePaymentByTransactionRef = async (transactionRef: string): Promise<OnlinePaymentDetails | null> => {
  try {
    // This would require a query with an index on paymentDetails.transactionRef
    // For now, return null as this is a placeholder
    console.log('Looking for online payment with transaction ref:', transactionRef);
    return null;
  } catch (error) {
    console.error('Error getting online payment:', error);
    return null;
  }
};
